import React from 'react';
import Article from '../components/Article';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CodeBlock from '../components/CodeBlock';

function HowToUsePythonInDevOps() {
  const articleData = {
    id: 11,
    image: "https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
    category: "DevOps",
    title: "How to Use Python in DevOps: A Practical Guide",
    excerpt: "Learn how Python can streamline your DevOps workflow with practical examples and essential use cases.",
    date: "June 5, 2023",
    readTime: "7 min read",
    author: {
      name: "Noman Masood",
      avatar: "/images/noman masood.jpg"
    },
    tags: ["Python", "DevOps", "Automation", "Infrastructure", "CI/CD"],
    content: (
      <>
        <p>
          Python has become an indispensable tool in the DevOps toolkit, offering a perfect balance of 
          readability, functionality, and vast library support. This guide explores the key ways you can 
          leverage Python to enhance your DevOps practices, with concrete examples to get you started.
        </p>
        
        <h2>Why Python for DevOps?</h2>
        <p>
          Before diving into specific use cases, let's understand why Python is particularly well-suited for DevOps:
        </p>
        
        <ul>
          <li><strong>Readability:</strong> Clean syntax makes scripts maintainable by the entire team</li>
          <li><strong>Cross-platform:</strong> Works across Linux, Windows, and macOS environments</li>
          <li><strong>Rich ecosystem:</strong> Extensive libraries for every DevOps task</li>
          <li><strong>API support:</strong> Easily interfaces with cloud platforms and tools</li>
          <li><strong>Low barrier to entry:</strong> Accessible to both developers and operations teams</li>
        </ul>
        
        <h2>1. Infrastructure as Code with Python</h2>
        <p>
          While tools like Terraform and CloudFormation dominate IaC, Python offers significant advantages 
          for more dynamic infrastructure needs.
        </p>
        
        <CodeBlock language="python" fileName="create_resources.py">
{`import boto3

def create_ec2_instances(count, instance_type, ami_id, key_name):
    """Create EC2 instances programmatically"""
    ec2 = boto3.resource('ec2')
    
    instances = ec2.create_instances(
        ImageId=ami_id,
        MinCount=count,
        MaxCount=count,
        InstanceType=instance_type,
        KeyName=key_name,
        TagSpecifications=[
            {
                'ResourceType': 'instance',
                'Tags': [
                    {'Key': 'Environment', 'Value': 'Production'},
                    {'Key': 'Name', 'Value': 'WebServer'}
                ]
            }
        ]
    )
    
    return [instance.id for instance in instances]

# Example usage
if __name__ == "__main__":
    instance_ids = create_ec2_instances(
        count=2, 
        instance_type='t2.micro',
        ami_id='ami-0c55b159cbfafe1f0',
        key_name='production-key'
    )
    print(f"Created instances: {instance_ids}")`}
        </CodeBlock>
        
        <p>
          For more sophisticated IaC needs, consider Python libraries like Pulumi or AWS CDK, 
          which provide the full power of Python while generating CloudFormation or Terraform under the hood.
        </p>
        
        <h2>2. Configuration Management</h2>
        <p>
          Python shines when you need to manage configuration across multiple servers or environments.
        </p>
        
        <CodeBlock language="python" fileName="configure_servers.py">
{`import paramiko
import yaml

def apply_configuration(servers, config_file):
    """Apply configuration to multiple servers"""
    # Load configuration
    with open(config_file, 'r') as f:
        config = yaml.safe_load(f)
    
    for server in servers:
        print(f"Configuring {server['hostname']}...")
        ssh = paramiko.SSHClient()
        ssh.set_missing_host_key_policy(paramiko.AutoAddPolicy())
        
        try:
            ssh.connect(
                hostname=server['hostname'],
                username=server['username'],
                key_filename=server['key_file']
            )
            
            # Apply configuration steps
            for step in config['steps']:
                cmd = step['command']
                print(f"Running: {cmd}")
                stdin, stdout, stderr = ssh.exec_command(cmd)
                print(stdout.read().decode())
                
                error = stderr.read().decode()
                if error:
                    print(f"Error: {error}")
            
        finally:
            ssh.close()

# Example usage
servers = [
    {'hostname': '10.0.1.10', 'username': 'ubuntu', 'key_file': '~/.ssh/prod.pem'},
    {'hostname': '10.0.1.11', 'username': 'ubuntu', 'key_file': '~/.ssh/prod.pem'},
]

apply_configuration(servers, 'web_server_config.yml')`}
        </CodeBlock>
        
        <h2>3. Automation of Repetitive Tasks</h2>
        <p>
          Automating routine operational tasks is where Python truly excels in DevOps.
        </p>
        
        <CodeBlock language="python" fileName="db_backup.py">
{`#!/usr/bin/env python3
import subprocess
import datetime
import os
import boto3

def backup_database(db_name, backup_dir, s3_bucket):
    """Back up a database and upload to S3"""
    # Generate backup filename with timestamp
    timestamp = datetime.datetime.now().strftime('%Y%m%d_%H%M%S')
    backup_file = f"{backup_dir}/{db_name}_{timestamp}.sql"
    
    # Create backup using pg_dump
    print(f"Creating backup of {db_name}...")
    result = subprocess.run(
        ['pg_dump', '-U', 'postgres', '-d', db_name, '-f', backup_file],
        capture_output=True,
        text=True
    )
    
    if result.returncode != 0:
        raise Exception(f"Backup failed: {result.stderr}")
    
    # Compress the backup file
    print("Compressing backup...")
    compressed_file = f"{backup_file}.gz"
    subprocess.run(['gzip', backup_file])
    
    # Upload to S3
    print(f"Uploading to S3 bucket {s3_bucket}...")
    s3 = boto3.client('s3')
    s3.upload_file(
        compressed_file,
        s3_bucket,
        f"backups/{os.path.basename(compressed_file)}"
    )
    
    print(f"Backup completed and uploaded: {compressed_file}")
    return compressed_file

if __name__ == "__main__":
    backup_database(
        db_name='production_db',
        backup_dir='/var/backups/postgres',
        s3_bucket='my-company-backups'
    )`}
        </CodeBlock>
        
        <h2>4. CI/CD Pipeline Integration</h2>
        <p>
          Python scripts can enhance your CI/CD pipelines, making them more dynamic and powerful.
        </p>
        
        <CodeBlock language="python" fileName="versioning.py">
{`import re
import subprocess
from datetime import datetime

def bump_version(version_file, release_type='patch'):
    """Bump semantic version in a file based on release type"""
    with open(version_file, 'r') as f:
        content = f.read()
    
    # Extract current version
    version_match = re.search(r'VERSION\s*=\s*[\'"](\d+\.\d+\.\d+)[\'"]', content)
    if not version_match:
        raise ValueError(f"Couldn't find version string in {version_file}")
    
    current_version = version_match.group(1)
    major, minor, patch = map(int, current_version.split('.'))
    
    # Update version based on release type
    if release_type == 'major':
        new_version = f"{major + 1}.0.0"
    elif release_type == 'minor':
        new_version = f"{major}.{minor + 1}.0"
    else:  # patch
        new_version = f"{major}.{minor}.{patch + 1}"
    
    # Update the file
    new_content = re.sub(
        r'VERSION\s*=\s*[\'"](\d+\.\d+\.\d+)[\'"]',
        f'VERSION = "{new_version}"',
        content
    )
    
    with open(version_file, 'w') as f:
        f.write(new_content)
    
    # Commit the change
    subprocess.run(['git', 'add', version_file])
    subprocess.run(['git', 'commit', '-m', f'Bump version to {new_version}'])
    subprocess.run(['git', 'tag', f'v{new_version}'])
    
    return new_version

# Example usage in a CI/CD pipeline
if __name__ == "__main__":
    new_version = bump_version('app/version.py', 'minor')
    print(f"Version bumped to {new_version}")`}
        </CodeBlock>
        
        <h2>5. Monitoring and Alerting</h2>
        <p>
          Create custom monitoring solutions when off-the-shelf tools don't quite fit your needs.
        </p>
        
        <CodeBlock language="python" fileName="api_monitor.py">
{`import requests
import time
import json
from datetime import datetime
import smtplib
from email.message import EmailMessage

def check_api_health(endpoints, notify_emails):
    """Monitor API endpoints and send alerts on failures"""
    results = []
    
    for endpoint in endpoints:
        url = endpoint['url']
        expected_status = endpoint.get('expected_status', 200)
        timeout = endpoint.get('timeout', 5)
        
        try:
            start_time = time.time()
            response = requests.get(url, timeout=timeout)
            response_time = time.time() - start_time
            
            status = {
                'endpoint': url,
                'timestamp': datetime.now().isoformat(),
                'status': 'healthy' if response.status_code == expected_status else 'unhealthy',
                'actual_status': response.status_code,
                'response_time': round(response_time * 1000, 2),  # in ms
                'error': None
            }
            
            if response.status_code != expected_status:
                status['error'] = f"Expected status {expected_status}, got {response.status_code}"
                send_alert(status, notify_emails)
            
        except requests.RequestException as e:
            status = {
                'endpoint': url,
                'timestamp': datetime.now().isoformat(),
                'status': 'error',
                'error': str(e)
            }
            send_alert(status, notify_emails)
        
        results.append(status)
    
    # Write results to log file
    with open('api_health.log', 'a') as f:
        for result in results:
            f.write(json.dumps(result) + '\\n')
    
    return results

def send_alert(status, emails):
    """Send email alert for API issues"""
    msg = EmailMessage()
    msg['Subject'] = f"API ALERT: {status['endpoint']} is {status['status']}"
    msg['From'] = 'monitoring@example.com'
    msg['To'] = ', '.join(emails)
    
    body = f"""
    API ALERT: {status['endpoint']}
    Status: {status['status']}
    Time: {status['timestamp']}
    
    Details:
    {json.dumps(status, indent=2)}
    """
    
    msg.set_content(body)
    
    # Send the email
    try:
        with smtplib.SMTP('smtp.example.com', 587) as server:
            server.starttls()
            server.login('alert@example.com', 'password')
            server.send_message(msg)
    except Exception as e:
        print(f"Failed to send alert email: {e}")

# Example usage in a cron job
if __name__ == "__main__":
    endpoints = [
        {'url': 'https://api.example.com/health', 'expected_status': 200},
        {'url': 'https://api.example.com/v2/status', 'expected_status': 200},
    ]
    
    notify_emails = ['devops@example.com', 'oncall@example.com']
    check_api_health(endpoints, notify_emails)`}
        </CodeBlock>
        
        <h2>Getting Started with Python in DevOps</h2>
        <p>
          To start integrating Python into your DevOps workflows:
        </p>
        
        <ol className="workflow-steps">
          <li><strong>Learn the basics:</strong> Ensure you understand Python fundamentals</li>
          <li><strong>Master key libraries:</strong> Focus on boto3 (AWS), requests, paramiko, and subprocess</li>
          <li><strong>Start small:</strong> Automate a single repetitive task before building complex systems</li>
          <li><strong>Use version control:</strong> Treat automation code with the same rigor as application code</li>
          <li><strong>Build reusable components:</strong> Create modules you can compose for different tasks</li>
        </ol>
        
        <h2>Conclusion</h2>
        <p>
          Python's versatility makes it a perfect fit for DevOps workflows. By starting with these fundamental 
          patterns, you can gradually build a toolkit of Python scripts and applications that streamline your 
          infrastructure management, automate repetitive tasks, and enhance your CI/CD pipelines.
        </p>
        
        <p>
          Remember that the goal isn't to replace specialized DevOps tools but to complement them – 
          use Python where its flexibility and simplicity provide the most value, especially for custom workflows 
          or when connecting different systems together.
        </p>
      </>
    )
  };

  return (
    <div className="article-page">
      <Navbar />
      <main>
        <Article article={articleData} />
      </main>
      <Footer />
    </div>
  );
}

export default HowToUsePythonInDevOps;
